import { err, ok, type Result } from "@repo/lib/result";
import { z } from "zod";

export const SYSTEM_STORY_SCHEMA_VERSION = 1 as const;

export const SYSTEM_STORY_IDS = [
  "story.play.sync",
  "story.pause.sync",
  "story.seek.sync",
  "story.tempo.sync",
  "story.key.sync",
  "story.reconnect.sync",
  "story.late_join.sync",
] as const;

export type SystemStoryId = (typeof SYSTEM_STORY_IDS)[number];

export const TRANSPORT_KINDS = ["aws-legacy", "partykit", "livekit"] as const;
export type SystemStoryTransportKind = (typeof TRANSPORT_KINDS)[number];

const StoryIdSchema = z.enum(SYSTEM_STORY_IDS);
const TransportKindSchema = z.enum(TRANSPORT_KINDS);

const NonEmptyString = z.string().min(1);
const NonNegativeNumber = z.number().finite().nonnegative();
const PositiveNumber = z.number().finite().positive();

export const SystemStoryCommandSchema = z.discriminatedUnion("storyId", [
  z.object({
    storyId: z.literal("story.play.sync"),
    commandId: NonEmptyString,
    issuedAtMs: NonNegativeNumber,
    payload: z.object({
      songId: NonEmptyString,
      fromPositionSec: NonNegativeNumber,
      rate: PositiveNumber,
      pitchSemitones: z.number().finite().min(-24).max(24),
    }),
  }),
  z.object({
    storyId: z.literal("story.pause.sync"),
    commandId: NonEmptyString,
    issuedAtMs: NonNegativeNumber,
    payload: z.object({
      atPositionSec: NonNegativeNumber,
    }),
  }),
  z.object({
    storyId: z.literal("story.seek.sync"),
    commandId: NonEmptyString,
    issuedAtMs: NonNegativeNumber,
    payload: z.object({
      toPositionSec: NonNegativeNumber,
      isPlaying: z.boolean(),
    }),
  }),
  z.object({
    storyId: z.literal("story.tempo.sync"),
    commandId: NonEmptyString,
    issuedAtMs: NonNegativeNumber,
    payload: z.object({
      rate: PositiveNumber,
    }),
  }),
  z.object({
    storyId: z.literal("story.key.sync"),
    commandId: NonEmptyString,
    issuedAtMs: NonNegativeNumber,
    payload: z.object({
      pitchSemitones: z.number().finite().min(-24).max(24),
    }),
  }),
  z.object({
    storyId: z.literal("story.reconnect.sync"),
    commandId: NonEmptyString,
    issuedAtMs: NonNegativeNumber,
    payload: z.object({
      participantId: NonEmptyString,
      disconnectMs: NonNegativeNumber,
      reconnectByMs: NonNegativeNumber,
    }),
  }),
  z.object({
    storyId: z.literal("story.late_join.sync"),
    commandId: NonEmptyString,
    issuedAtMs: NonNegativeNumber,
    payload: z.object({
      participantId: NonEmptyString,
      joinAtPositionSec: NonNegativeNumber,
      expectCatchUpWithinMs: NonNegativeNumber,
    }),
  }),
]);

export type SystemStoryCommand = z.infer<typeof SystemStoryCommandSchema>;

export const SystemStoryCommandArtifactSchema = z.object({
  commandId: NonEmptyString,
  storyId: StoryIdSchema,
  transport: TransportKindSchema,
  wireOp: NonEmptyString,
  wirePayload: z.unknown(),
  issuedAtMs: NonNegativeNumber,
  sentAtMs: NonNegativeNumber,
  receivedAtMs: NonNegativeNumber.optional(),
  appliedAtMs: NonNegativeNumber.optional(),
  timeoutAtMs: NonNegativeNumber.optional(),
  timedOut: z.boolean(),
  notes: z.string().optional(),
});

export type SystemStoryCommandArtifact = z.infer<typeof SystemStoryCommandArtifactSchema>;

export const SystemStoryResultSchema = z.object({
  schemaVersion: z.literal(SYSTEM_STORY_SCHEMA_VERSION),
  storyId: StoryIdSchema,
  scenario: z.object({
    scenarioId: NonEmptyString,
    iteration: z.number().int().positive(),
    participantCount: z.number().int().positive(),
    timeoutMs: NonNegativeNumber,
  }),
  transport: z.object({
    kind: TransportKindSchema,
    endpoint: NonEmptyString,
    roomId: NonEmptyString,
  }),
  envFingerprint: z.object({
    commitSha: NonEmptyString,
    browserUserAgent: NonEmptyString,
    runStartedAtIso: NonEmptyString,
    configHash: NonEmptyString,
  }),
  metrics: z.object({
    commandCount: z.number().int().nonnegative(),
    commandDeliveryLatencyP99Ms: NonNegativeNumber,
    intraCommandSkewP99Ms: NonNegativeNumber,
    timeoutCount: z.number().int().nonnegative(),
  }),
  gates: z.object({
    hasAllArtifacts: z.boolean(),
    noTimeouts: z.boolean(),
    measuredDeliveryLatency: z.boolean(),
    measuredIntraCommandSkew: z.boolean(),
  }),
  passed: z.boolean(),
  artifacts: z.array(SystemStoryCommandArtifactSchema),
});

export type SystemStoryResult = z.infer<typeof SystemStoryResultSchema>;

export interface SystemStoryValidationError {
  readonly kind: "systemStoryValidationError";
  readonly issues: readonly string[];
}

export function validateSystemStoryResult(
  input: unknown,
): Result<SystemStoryResult, SystemStoryValidationError> {
  const parsed = SystemStoryResultSchema.safeParse(input);
  if (parsed.success) {
    return ok(parsed.data);
  }
  return err({
    kind: "systemStoryValidationError",
    issues: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
  });
}

function percentile(values: readonly number[], percentileValue: number): number {
  if (values.length === 0) {
    return 0;
  }
  const sorted = [...values].sort((left, right) => left - right);
  const rank = Math.ceil((percentileValue / 100) * sorted.length) - 1;
  const index = Math.max(0, Math.min(sorted.length - 1, rank));
  return sorted[index] ?? 0;
}

function toIntraCommandSkew(artifacts: readonly SystemStoryCommandArtifact[]): readonly number[] {
  return artifacts
    .map((artifact) => {
      if (artifact.appliedAtMs === undefined || artifact.receivedAtMs === undefined) {
        return null;
      }
      return Math.max(0, artifact.appliedAtMs - artifact.receivedAtMs);
    })
    .filter((value): value is number => value !== null);
}

export interface BuildSystemStoryResultInput {
  readonly storyId: SystemStoryId;
  readonly scenario: SystemStoryResult["scenario"];
  readonly transport: SystemStoryResult["transport"];
  readonly envFingerprint: SystemStoryResult["envFingerprint"];
  readonly artifacts: readonly SystemStoryCommandArtifact[];
}

export function buildSystemStoryResult(input: BuildSystemStoryResultInput): SystemStoryResult {
  const deliveryLatencies = input.artifacts
    .map((artifact) => {
      if (artifact.receivedAtMs === undefined) {
        return null;
      }
      return Math.max(0, artifact.receivedAtMs - artifact.sentAtMs);
    })
    .filter((value): value is number => value !== null);
  const intraCommandSkews = toIntraCommandSkew(input.artifacts);
  const timeoutCount = input.artifacts.filter((artifact) => artifact.timedOut).length;
  const gates = {
    hasAllArtifacts: input.artifacts.length > 0,
    noTimeouts: timeoutCount === 0,
    measuredDeliveryLatency: deliveryLatencies.length > 0,
    measuredIntraCommandSkew: intraCommandSkews.length > 0,
  };

  return {
    schemaVersion: SYSTEM_STORY_SCHEMA_VERSION,
    storyId: input.storyId,
    scenario: input.scenario,
    transport: input.transport,
    envFingerprint: input.envFingerprint,
    metrics: {
      commandCount: input.artifacts.length,
      commandDeliveryLatencyP99Ms: percentile(deliveryLatencies, 99),
      intraCommandSkewP99Ms: percentile(intraCommandSkews, 99),
      timeoutCount,
    },
    gates,
    passed:
      gates.hasAllArtifacts &&
      gates.noTimeouts &&
      gates.measuredDeliveryLatency &&
      gates.measuredIntraCommandSkew,
    artifacts: [...input.artifacts],
  };
}

export function renderSystemStoryResultMarkdown(result: SystemStoryResult): string {
  return [
    `## ${result.storyId} — ${result.transport.kind}`,
    "",
    `- Scenario: \`${result.scenario.scenarioId}\` iteration ${result.scenario.iteration}`,
    `- Participants: ${result.scenario.participantCount}`,
    `- Endpoint: \`${result.transport.endpoint}\``,
    `- Room: \`${result.transport.roomId}\``,
    `- Command count: ${result.metrics.commandCount}`,
    `- p99 delivery latency: ${result.metrics.commandDeliveryLatencyP99Ms.toFixed(2)} ms`,
    `- p99 intra-command skew: ${result.metrics.intraCommandSkewP99Ms.toFixed(2)} ms`,
    `- Timeouts: ${result.metrics.timeoutCount}`,
    `- Passed: ${result.passed ? "yes" : "no"}`,
  ].join("\n");
}
